import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import axios from 'axios'
import type { Card, User } from '../types'
import { GRID_COLS } from '../types'
import CardRenderer from '../components/cards/CardRenderer'
import { useAuthStore } from '../store/authStore'
import { calculateCellSize } from '../utils/gridLayout'
import { useThemeStore, type ThemeMode } from '../store/themeStore'
import { useCardStore } from '../store/cardStore'
import { useAudioPlayerStore } from '../store/audioPlayerStore'
import GlobalAudioPlayer from '../components/GlobalAudioPlayer'

function hashToUint32(input: string): number {
  let h = 2166136261
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

interface ProfileData {
  user: Pick<User, 'name' | 'subdomain' | 'profile_picture'>
  cards: Card[]
}

function AnimatedProfileCard({
  card,
  cellSize,
  topOffsetPx,
  cardPadding,
  paletteBg,
  paletteFg,
  cardRadiusCSS,
  theme,
}: {
  card: Card
  cellSize: number
  topOffsetPx: number
  cardPadding: number
  paletteBg: string
  paletteFg: string
  cardRadiusCSS: string
  theme: ThemeMode | undefined
}) {
  const isPublicTransitioning = useCardStore((state) => state.isPublicTransitioning)

  return (
    <motion.div
      className="absolute"
      style={{
        left: card.grid_x * cellSize,
        top: topOffsetPx + card.grid_y * cellSize,
        width: card.grid_width * cellSize,
        height: card.grid_height * cellSize,
        padding: cardPadding,
        ['--card-bg' as any]: paletteBg,
        ['--card-fg' as any]: paletteFg,
        ['--card-radius' as any]: cardRadiusCSS,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: isPublicTransitioning ? 0 : 1 }}
      transition={{ duration: 0.15, ease: 'linear' }}
    >
      <CardRenderer card={card} editMode={false} cellSize={cellSize} themeOverride={theme} />
    </motion.div>
  )
}

function getSubdomainFromHostname(hostname: string): string {
  const host = hostname.split(':')[0].toLowerCase()
  if (!host) return ''

  if (host === 'oda.bio' || host === 'www.oda.bio') return ''

  if (host.endsWith('.oda.bio')) {
    const sub = host.slice(0, -'.oda.bio'.length)
    return sub === 'www' ? '' : sub
  }

  return ''
}

export default function ProfilePage({ subdomainOverride }: { subdomainOverride?: string }) {
  const { subdomain: routeSubdomain, page: routePage } = useParams<{ subdomain: string; page: string }>()
  const subdomain = subdomainOverride || routeSubdomain || getSubdomainFromHostname(window.location.hostname)
  const pageSlug = routePage || (subdomainOverride ? window.location.pathname.split('/').filter(Boolean)[0] : undefined)
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore()
  const [data, setData] = useState<ProfileData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [themeProfileCard, setThemeProfileCard] = useState<Card | null>(null)
  const { setQueueFromCards } = useAudioPlayerStore()

  useEffect(() => {
    const hostSub = getSubdomainFromHostname(window.location.hostname)
    const isOnSubdomainHost = !!hostSub
    if (!isOnSubdomainHost) return
    if (authLoading) return

    if (isAuthenticated && user?.subdomain && hostSub && user.subdomain === hostSub) {
      window.location.href = 'https://oda.bio/edit'
    }
  }, [authLoading, isAuthenticated, user])

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const params = pageSlug ? `?page=${pageSlug}` : ''
        const response = await axios.get(`/api/profile/${subdomain}${params}`)
        setData(response.data)
      } catch (err: any) {
        if (err.response?.status === 403 && err.response?.data?.is_private) {
          setError('Bu profil gizlidir.')
        } else {
          setError('Profile not found')
        }
      } finally {
        setLoading(false)
      }
    }

    if (subdomain) {
      fetchProfile()
    } else {
      setLoading(false)
      setError('Profile not found')
    }
  }, [subdomain, pageSlug])

  useEffect(() => {
    setThemeProfileCard(null)
    if (!subdomain) return
    if (!pageSlug) return

    let cancelled = false
    const fetchThemeSource = async () => {
      try {
        const res = await axios.get(`/api/profile/${subdomain}`)
        const cards = (res.data?.cards || []) as Card[]
        const pc = cards.find((c) => c.card_type === 'profile') || null
        if (!cancelled) setThemeProfileCard(pc)
      } catch {
        if (!cancelled) setThemeProfileCard(null)
      }
    }
    fetchThemeSource()
    return () => {
      cancelled = true
    }
  }, [subdomain, pageSlug])

  const [paletteSeed, setPaletteSeed] = useState(0)

  useEffect(() => {
    if (!data) return

    const prevTitle = document.title
    const touched: Array<{ el: HTMLMetaElement; prevContent: string | null }> = []

    const upsertMeta = (selector: string, attrs: Record<string, string>, content: string) => {
      let el = document.head.querySelector(selector) as HTMLMetaElement | null
      if (!el) {
        el = document.createElement('meta')
        for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v)
        document.head.appendChild(el)
      }
      touched.push({ el, prevContent: el.getAttribute('content') })
      el.setAttribute('content', content)
    }

    const displayName = data.user?.name || data.user?.subdomain || 'oda.bio'
    const title = pageSlug
      ? `${displayName} · /${pageSlug} | oda.bio`
      : `${displayName} | oda.bio`
    const description = pageSlug
      ? `${displayName} on oda.bio · /${pageSlug}`
      : `${displayName} on oda.bio`
    const url = window.location.href
    const image = data.user?.profile_picture || ''

    document.title = title
    upsertMeta('meta[name="description"]', { name: 'description' }, description)
    upsertMeta('meta[property="og:title"]', { property: 'og:title' }, title)
    upsertMeta('meta[property="og:description"]', { property: 'og:description' }, description)
    upsertMeta('meta[property="og:type"]', { property: 'og:type' }, 'profile')
    upsertMeta('meta[property="og:url"]', { property: 'og:url' }, url)
    if (image) upsertMeta('meta[property="og:image"]', { property: 'og:image' }, image)
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, image ? 'summary_large_image' : 'summary')
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, title)
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description' }, description)
    if (image) upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, image)


    const profileCard = data.cards.find(c => c.card_type === 'profile') || themeProfileCard
    const nextTheme = (profileCard?.content as any)?.theme as ThemeMode | undefined
    const nextPaletteSeedRaw = (profileCard?.content as any)?.paletteSeed
    const nextPaletteSeed = typeof nextPaletteSeedRaw === 'number' && Number.isFinite(nextPaletteSeedRaw) ? nextPaletteSeedRaw : 0
    const nextColorPaletteRaw = (profileCard?.content as any)?.colorPalette
    const nextColorPalette = typeof nextColorPaletteRaw === 'number' && nextColorPaletteRaw >= 0 && nextColorPaletteRaw <= 3 ? nextColorPaletteRaw : 0

    if (nextTheme) {
      document.documentElement.setAttribute('data-theme', nextTheme)
      document.documentElement.setAttribute('data-palette', String(nextColorPalette))
      useThemeStore.setState({ theme: nextTheme, paletteSeed: nextPaletteSeed, colorPalette: nextColorPalette })
    }
    setPaletteSeed(nextPaletteSeed)

    const photoCards = data.cards.filter(c => c.card_type === 'photo')
    const preloadImages: string[] = []
    for (const card of photoCards.slice(0, 3)) {
      const { imageUrl, images } = card.content
      const effectiveImages = (images && Array.isArray(images) && images.length > 0) ? images : (imageUrl ? [imageUrl] : [])
      if (effectiveImages[0]) preloadImages.push(effectiveImages[0])
    }

    if (preloadImages.length === 0) return

    const r2Domain = new URL(preloadImages[0]).origin
    const links: HTMLLinkElement[] = []

    const preconnect = document.createElement('link')
    preconnect.rel = 'preconnect'
    preconnect.href = r2Domain
    preconnect.crossOrigin = 'anonymous'
    document.head.appendChild(preconnect)
    links.push(preconnect)

    preloadImages.forEach((src, i) => {
      const preload = document.createElement('link')
      preload.rel = 'preload'
      preload.as = 'image'
      preload.href = src
      if (i === 0) preload.setAttribute('fetchpriority', 'high')
      document.head.appendChild(preload)
      links.push(preload)
    })

    return () => {
      links.forEach(link => link.remove())

      document.title = prevTitle
      touched.forEach(({ el, prevContent }) => {
        if (prevContent === null) el.removeAttribute('content')
        else el.setAttribute('content', prevContent)
      })

    }
  }, [data, themeProfileCard])

  useEffect(() => {
    setQueueFromCards(data?.cards || [])
  }, [data, setQueueFromCards])

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--app-bg)] text-[var(--app-fg)] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-current border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[var(--app-bg)] text-[var(--app-fg)] flex flex-col items-center justify-center px-6">
        <h1 className="font-serif text-4xl font-bold mb-4">404</h1>
        <p className="font-serif opacity-80">This page doesn't exist yet.</p>
      </div>
    )
  }

  const cellSize = calculateCellSize(window.innerWidth, window.innerHeight, 0)
  const gridWidth = cellSize * GRID_COLS

  const topOffsetPx = Math.round(window.innerHeight * 0.03)

  const maxY = Math.max(...data.cards.map(c => c.grid_y + c.grid_height), 32)
  const gridHeight = cellSize * maxY

  const profileCard = data.cards.find(c => c.card_type === 'profile') || themeProfileCard
  const theme = (profileCard?.content as any)?.theme as ThemeMode | undefined
  const renderColorPaletteRaw = (profileCard?.content as any)?.colorPalette
  const renderColorPalette =
    typeof renderColorPaletteRaw === 'number' && renderColorPaletteRaw >= 0 && renderColorPaletteRaw <= 3
      ? renderColorPaletteRaw
      : 0

  return (
    <div className="min-h-screen bg-[var(--app-bg)] text-[var(--app-fg)] flex justify-center px-4 sm:px-8">
      <motion.div
        key={`public-page-${pageSlug || '__main__'}`}
        className="relative"
        style={{
          width: gridWidth,
          minHeight: gridHeight,
          paddingTop: `${topOffsetPx}px`
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15, ease: 'linear' }}
      >
        {data.cards.map((card) => {
          const forcedPaletteIndexRaw = (card.content as any)?.paletteIndex
          const forcedPaletteIndex =
            forcedPaletteIndexRaw === 1 || forcedPaletteIndexRaw === 2 || forcedPaletteIndexRaw === 3 || forcedPaletteIndexRaw === 4
              ? forcedPaletteIndexRaw
              : null
          const paletteIndex = forcedPaletteIndex ?? ((hashToUint32(`${card.id}-${paletteSeed}`) % 4) + 1)
          const paletteBg = `var(--card-bg-${paletteIndex})`
          const paletteFg =
            theme === 'neo'
              ? renderColorPalette === 0
                ? paletteIndex === 1
                  ? '#e8f5ee'
                  : '#011910'
                : renderColorPalette === 1
                  ? paletteIndex === 1
                    ? '#e0e8f5'
                    : '#0a0f1e'
                  : renderColorPalette === 2
                    ? paletteIndex === 1
                      ? '#f0e8f5'
                      : '#1a0a20'
                    : paletteIndex === 1
                      ? '#f5e8ef'
                      : '#1a0a14'
              : theme === 'glass'
                ? 'rgba(10,10,10,0.92)'
                : theme === 'wood'
                  ? renderColorPalette === 1
                    ? paletteIndex === 4
                      ? 'rgba(243,231,213,0.96)'
                      : 'rgba(41,40,49,0.96)'
                    : renderColorPalette === 2
                      ? paletteIndex >= 3
                        ? 'rgba(16,31,38,0.96)'
                        : 'rgba(232,249,244,0.96)'
                      : 'rgba(243,231,213,0.96)'
                  : '#0a0a0a'

          const cardRadius = ((profileCard?.content as any)?.cardRadius as 'sharp' | 'rounded') || 'sharp'
          const cardPadding = cardRadius === 'sharp' ? 1 : 4
          const cardRadiusCSS = cardRadius === 'sharp' ? '0px' : '16px'
          return (
            <AnimatedProfileCard
              key={card.id}
              card={card}
              cellSize={cellSize}
              topOffsetPx={topOffsetPx}
              cardPadding={cardPadding}
              paletteBg={paletteBg}
              paletteFg={paletteFg}
              cardRadiusCSS={cardRadiusCSS}
              theme={theme}
            />
          )
        })}
      </motion.div>
      <GlobalAudioPlayer bottomOffset={38} maxWidthPx={gridWidth} />
    </div>
  )
}
